import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Refresh as RefreshIcon, Chat as ChatIcon } from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

function Turns() {
  const navigate = useNavigate();

  const formatNumber = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const handleBack = () => {
    navigate('/dashboard');
  };

  const [transactions, setTransactions] = useState([]);
  const [pastDays, setPastDays] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const handleRowClick = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const fetchTransactions = async (days = 2) => {
    setLoading(true);
    setExpandedRow(null);
    try {
      const transactionsRef = collection(db, 'bot-session-transactions');
      const pastDate = new Date();
      pastDate.setDate(pastDate.getDate() - days);

      const q = query(
        transactionsRef,
        where('dateTime', '>=', pastDate.toISOString()),
        orderBy('dateTime', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const results = [];
      querySnapshot.forEach(doc => {
        results.push({ id: doc.id, ...doc.data() });
      });

      setTransactions(results);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(2);
  }, []);

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4">
            Recent Transactions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1">Past Days:</Typography>
          <input 
            type="number" 
            value={pastDays} 
            onChange={(e) => {
              setPastDays(Number(e.target.value)); 
              fetchTransactions(Number(e.target.value));
            }}
            min="1"
            max="7"
            style={{ width: '60px', padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
          <IconButton 
            onClick={() => fetchTransactions(pastDays)}
            disabled={loading}
            size="large"
            sx={{ 
              padding: 1,
              borderRadius: 1,
              width: 'fit-content'
            }}
            aria-label="refresh"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>
      {!loading && transactions.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
          No transactions found
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell>Prompt Name</TableCell>
                <TableCell>User Message</TableCell>
                <TableCell>Response</TableCell>
                <TableCell align="right">LLM Requests</TableCell>
                <TableCell align="right">Candidates Tokens</TableCell>
                <TableCell align="right">Prompt Tokens</TableCell>
                <TableCell align="right">Total Tokens</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <React.Fragment key={transaction.id}>
                  <TableRow 
                    onClick={() => handleRowClick(transaction.id)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <TableCell sx={{ verticalAlign: 'top', width: 48 }}>
                      <IconButton
                        onClick={e => {
                          e.stopPropagation();
                          if (transaction.sessionId) {
                            navigate(`/conversations/${transaction.sessionId}`);
                          }
                        }}
                        aria-label="Go to conversation"
                        size="small"
                      >
                        <ChatIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {transaction.userId}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {new Date(transaction.dateTime).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {transaction.promptName || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, whiteSpace: 'pre-wrap', verticalAlign: 'top' }}>
                      {transaction.userUtterance || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, verticalAlign: 'top' }}>
                      <Box sx={{ p: 1 }}>
                        <ReactMarkdown>{transaction.response || 'N/A'}</ReactMarkdown>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {transaction.totalLlmRequests || '0'}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {formatNumber(transaction.candidatesTokenCount || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {formatNumber(transaction.promptTokenCount || 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      {formatNumber(transaction.totalTokenCount || 0)}
                    </TableCell>
                  </TableRow>
                  {expandedRow === transaction.id && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ py: 2, bgcolor: 'action.selected' }}>
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'minmax(0, 0.7fr) minmax(0, 0.3fr)',
                          gap: 2,
                          px: 2,
                          width: '100%'
                        }}>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              System Instructions:
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                              <ReactMarkdown>{transaction.systemInstructions || 'N/A'}</ReactMarkdown>
                            </Paper>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Prompt:
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                              <ReactMarkdown>{transaction.prompt || 'N/A'}</ReactMarkdown>
                            </Paper>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default Turns;
